import DataQuery from "data/DataQuery";
import { Consumption_ConsumptionRealDetail, PlaceOfDelivery_Consumption, PlaceOfDelivery_ConsumptionExport, PlaceOfDelivery_ContractsDuration } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import IFileService from "data/IFileService";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { getPlaceOfDeliveryContractsDurationParams } from "features/shared/helpers/queryParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PlaceOfDeliveryConsumptionFullDTO } from "../dtos/PlaceOfDeliveryConsumptionFullDTO";
import { useTranslation } from "react-i18next";
import PlaceOfDeliveryMonthConsumptionGrid from "../components/PlaceOfDeliveryMonthConsumptionGrid";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import MeasuredDataGraphCard from "features/shared/components/graphs/MeasuredDataGraphCard";
import MeasuredData from "features/shared/components/measuredData/MeasuredData";
import { getDownloadUrlByContractType, hasMeasuredData } from "features/shared/components/measuredData/measuredDataHelper";
import MeasuredDataHistoryHeader from "features/shared/components/measuredData/MeasuredDataHistoryHeader";

type Props = {
  placeOfDeliveryId: string;
  isProduction: boolean;
  year?: number;
}

const PlaceOfDeliveryConsumptionDetailSection = ({ placeOfDeliveryId, isProduction, year }: Props) => {
  // DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);
  
  // translations
  const tPlaceOfDelivery = useTranslation("placeOfDelivery\\placeOfDelivery").t;
  
  // store
  const customer = useSelector<IAppState, CustomerState>( (store) => store.customer).customer;

  // state
  const [selectedYear, setSelectedYear] = useState<number>(year ?? 0);

  // fetch
  const contractsDurationQuery: DataQuery = {
    url: PlaceOfDelivery_ContractsDuration,
    params: getPlaceOfDeliveryContractsDurationParams(customer.customerId, placeOfDeliveryId, undefined, isProduction)
  };
  const { data: years, loadStatus: yearsLoadStatus } =
    useQuery<number[]>(contractsDurationQuery, [], []);

  const consumptionsQuery: DataQuery = {
    url: PlaceOfDelivery_Consumption,
    routeParams: new URLSearchParams({
      placeOfDeliveryId: placeOfDeliveryId,
      year: selectedYear.toString(),
    }),
    params: new URLSearchParams([
      ["CustomerId", customer.customerId!],
      ["IsProduction", `${isProduction}`],
    ])
  };
  const { data: consumptions, loadStatus: consumptionsLoadStatus } =
    useQuery<PlaceOfDeliveryConsumptionFullDTO>(
      consumptionsQuery,
      {},
      [selectedYear, years, customer.customerId],
      { useTracker: true },
      { apply: true, condition: () => (years.length > 0) && (selectedYear !== undefined && selectedYear !== 0) }
    );

  // side-effects
  useEffect(() => {
    if (years.length > 0) {
      if (!years.includes(selectedYear)) {
        setSelectedYear(years[years.length - 1]);
      }
    }
  }, [years]);

  // handlers
  const downloadConsumptionExcelHandler = () => {
    let query: DataQuery = {
      url: PlaceOfDelivery_ConsumptionExport,
      routeParams: new URLSearchParams({
        placeOfDeliveryId: placeOfDeliveryId,
        year: selectedYear.toString(),
      }),
      params: new URLSearchParams({
        CustomerId: customer.customerId!,
        IsProduction: isProduction ? `${isProduction}` : "false",
      }),
    };
    
    fileService.downloadFile(query);
  }  

  // render
  return (
    <NoContentWrapper 
      data={years} 
      loadStatus={yearsLoadStatus}
      noContentAsAlert
    >
      <MeasuredDataHistoryHeader 
        years={years}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        onDownloadExcelClick={downloadConsumptionExcelHandler}
      />
      <NoContentWrapper
        data={consumptions}
        loadStatus={consumptionsLoadStatus}
        noContentAsAlert
      >
        <MeasuredDataGraphCard 
          commodityDetail={consumptions.commodityDetail!} 
          planValues={isProduction ? undefined : consumptions.planConsumption}
          realValues={consumptions.realConsumption!}       
          showLegend
          adaptiveContainerHeight={false}   
          isProduction={isProduction}
        />

        <PlaceOfDeliveryMonthConsumptionGrid 
          placeOfDeliveryConsumption={consumptions} 
          isProduction={isProduction}
        />

        <RenderWrapper
          isVisible={() =>
            hasMeasuredData(consumptions.contractType!, consumptions.meterType!)
          }
        >
          <MeasuredData
            title={tPlaceOfDelivery("placeOfDeliveryConsumptionDetailSection.profileData")}
            commodityDetail={consumptions.commodityDetail!}
            from={new Date(years[0], 0, 1)}
            to={new Date(years[years.length - 1], 11, 31)}
            year={selectedYear}
            isProduction={isProduction}
            graphDataUrl={Consumption_ConsumptionRealDetail}
            downloadDataUrl={getDownloadUrlByContractType(
              consumptions.contractType!,
              isProduction,
              consumptions.meterType
            )}
            identificationParams={
              new URLSearchParams({
                PlaceOfDeliveryId: placeOfDeliveryId,
                CustomerId: customer.customerId!,
              })
            }
          />
        </RenderWrapper>
      </NoContentWrapper>
    </NoContentWrapper>
  )
}

export default PlaceOfDeliveryConsumptionDetailSection;